import React, { createContext, useContext, useState, ReactNode, useCallback, Fragment } from 'react';
import { CModal, CModalHeader, CModalBody, CModalFooter, CButton, CAlert } from '@coreui/react';
import CIcon from '@coreui/icons-react';

type ModalActionType = 'primary' | 'info' | 'warning' | 'error' | 'danger' | 'secondary';

type ModalActionVisual = {
  text: string;
  type?: ModalActionType;
  onClick?: () => void;
};

export type ModalAction<T> = ModalActionVisual & {
  result: T;
};

type ModalMessageSegment =
  | string
  | {
      message: string;
      icon?: string[];
      className?: string;
      alertColor?: string;
    };

export interface ModalOptions {
  title: string;
  message: ModalMessageSegment | ModalMessageSegment[];
  closeOnBackdropClick?: boolean;
  closeOnEscapeKey?: boolean;
  headerClassName?: string;
}

type ShowModalInput<T extends string | number | boolean | object> = ModalOptions & {
  actions: readonly ModalAction<T>[];
};
type ShowModalConfirmationInput = Partial<ModalOptions> & {
  confirm?: Partial<ModalActionVisual>;
};
type ShowModalErrorInput = Partial<ModalOptions> &
  Pick<ModalOptions, 'message'> & {
    additionalActions?: readonly ModalActionVisual[];
  };

interface ModalContextType {
  showModal: <T extends string | number | boolean | object>(i: ShowModalInput<T>) => Promise<T | undefined>;

  showModalConfirmation: (i?: ShowModalConfirmationInput) => Promise<boolean>;

  showModalError: (i: ShowModalErrorInput) => Promise<void>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

// Internal request type in the queue needs to be generic
interface ModalRequest<T> extends ModalOptions {
  actions: readonly ModalAction<T>[];
  resolve: (value: T | undefined) => void;
}

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  // Use 'unknown' for the queue state, which is safer than 'any'
  const [queue, setQueue] = useState<ModalRequest<unknown>[]>([]);

  const showModal = useCallback(<T extends string | number | boolean | object>(options: ShowModalInput<T>): Promise<T | undefined> => {
    return new Promise<T | undefined>((resolve) => {
      // Cast options to the correct internal request type
      const request = { ...options, resolve } as ModalRequest<unknown>;
      setQueue((prev) => [...prev, request]);
    });
  }, []);

  const handleAction = useCallback(
    (result?: unknown) => {
      const [current, ...rest] = queue;
      // Resolve with the action's result or undefined if closed
      current?.resolve(result);
      setQueue(rest);
    },
    [queue],
  );

  const showModalConfirmation = useCallback(
    async (options?: ShowModalConfirmationInput): Promise<boolean> => {
      const modalResult = await showModal({
        closeOnBackdropClick: false,
        closeOnEscapeKey: true,
        ...options,
        title: options?.title || 'Conferma',
        message: options?.message || 'Sei sicuro di voler procedere ?',
        actions: [
          {
            result: 'cancel',
            text: 'Annulla',
            type: 'secondary',
          },
          {
            result: 'confirm',
            text: options?.confirm?.text || 'Procedi',
            type: options?.confirm?.type || 'primary',
          },
        ] as const,
      });
      return modalResult === 'confirm';
    },
    [showModal],
  );

  const showModalError = useCallback(
    async (options: ShowModalErrorInput): Promise<void> => {
      await showModal({
        closeOnBackdropClick: false,
        closeOnEscapeKey: true,
        headerClassName: 'bg-danger text-white',
        ...options,
        title: options?.title || 'Attenzione',
        message: options?.message,
        actions: [
          ...(options?.additionalActions ?? []).map((a) => ({ ...a, result: 'action' })),
          {
            result: 'ok',
            text: 'Ok',
            type: 'secondary',
          },
        ] as const,
      });
      return;
    },
    [showModal],
  );

  const currentRequest = queue[0];

  const ctxValue: ModalContextType = {
    showModal,
    showModalConfirmation,
    showModalError,
  };

  const renderMessageSegment = (segment: ModalMessageSegment | ModalMessageSegment[]): ReactNode => {
    if (Array.isArray(segment)) {
      return segment.map((s, i) => {
        return (
          <Fragment key={'message-segment-' + i}>
            <p>{renderMessageSegment(s)}</p>
          </Fragment>
        );
      });
    }

    if (typeof segment === 'string') {
      return <>{segment}</>;
    }
    let out = <>{segment.message}</>;
    if (segment.icon) {
      out = (
        <>
          <CIcon icon={segment.icon} className={'me-2'}></CIcon> {out}
        </>
      );
    }
    if (segment.className) {
      out = <span className={segment.className}>{out}</span>;
    }
    if (segment.alertColor) {
      out = <CAlert color={segment.alertColor}>{out}</CAlert>;
    }
    return out;
  };

  return (
    <ModalContext.Provider value={ctxValue}>
      {children}
      {currentRequest && (
        <CModal
          className={'modal-managed-dialog'}
          visible
          // Pass 'undefined' to handleAction when closed via backdrop/escape key
          onClose={() => handleAction(undefined)}
          // Use a 'static' backdrop if closeOnBackdropClick is explicitly false
          backdrop={currentRequest.closeOnBackdropClick === false ? 'static' : true}
          // Pass the closeOnEscapeKey value to the keyboard prop, defaulting to true
          keyboard={currentRequest.closeOnEscapeKey ?? true}
        >
          <CModalHeader className={currentRequest.headerClassName}>{currentRequest.title}</CModalHeader>
          <CModalBody>{renderMessageSegment(currentRequest.message)}</CModalBody>
          <CModalFooter>
            {currentRequest.actions.map((action, idx) => (
              <CButton
                key={idx}
                color={mapTypeToColor(action.type)}
                onClick={() => {
                  if (action.onClick) {
                    action.onClick();
                  } else {
                    return handleAction(action.result);
                  }
                }}
                className={mapTypeToButtonClasses(action.type)}
              >
                {action.text}
              </CButton>
            ))}
          </CModalFooter>
        </CModal>
      )}
    </ModalContext.Provider>
  );
};

// This helper function remains the same
const mapTypeToColor = (type?: ModalActionType): string => {
  switch (type) {
    case 'danger':
    case 'error':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'secondary':
      return 'secondary';
    case 'info':
    case 'primary':
    default:
      return 'primary';
  }
};

const mapTypeToButtonClasses = (type?: ModalActionType): string => {
  switch (type) {
    case 'danger':
    case 'error':
      return 'text-white';
    case 'warning':
      return '';
    case 'info':
    case 'primary':
    default:
      return '';
  }
};

// This custom hook remains the same
export const useModal = (): ModalContextType => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within a ModalProvider');
  return ctx;
};
